import React, { useState, useEffect, useMemo } from "react";
import {
  Globe,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  User,
} from "lucide-react";
import toast from "react-hot-toast";
import { useUserAuth } from "../../context/UserAuthContext";
import {
  fetchUserProfileService,
  updateUserProfileService,
} from "../../services/user/userService";
import { isValidEmail } from "../../utils/validators";

const inputClass =
  "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 shadow-sm transition placeholder:text-stone-400 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20";

const labelClass = "mb-1.5 block text-xs font-semibold text-stone-600";

function isValidPhoneDigits(phone) {
  const d = String(String(phone || "").replace(/\D/g, ""));
  return d.length >= 8 && d.length <= 15;
}

function initials(name, email) {
  const n = String(name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return n.slice(0, 2).toUpperCase();
  }
  const e = String(email || "").trim();
  return e ? e.slice(0, 2).toUpperCase() : "?";
}

function InfoRow({ icon: Icon, label, value, required }) {
  const display = value?.trim() ? value : "Not set";
  const empty = !value?.trim();
  return (
    <div className="flex gap-3 rounded-xl border border-stone-100 bg-stone-50/60 p-4 transition hover:border-stone-200/80">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-orange-600 shadow-sm ring-1 ring-stone-200/60">
        <Icon className="h-4 w-4" strokeWidth={2} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {label}
          {required ? <span className="text-orange-600"> *</span> : null}
        </p>
        <p
          className={`mt-0.5 text-sm font-medium break-words ${
            empty ? "text-stone-400 italic" : "text-stone-900"
          }`}
        >
          {display}
        </p>
      </div>
    </div>
  );
}

const emptyUser = () => ({
  name: "",
  email: "",
  phone: "",
  addressLine: "",
  city: "",
  stateInfo: "",
  country: "India",
});

const UserProfile = () => {
  const { isAuthenticated, setUser: setAuthUser, user: authUser } = useUserAuth();
  const [user, setUser] = useState(emptyUser);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editSnapshot, setEditSnapshot] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      try {
        const data = await fetchUserProfileService();
        if (!data) return;

        setUser({
          name: data.name ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          addressLine: data.addressLine ?? "",
          city: data.city ?? "",
          stateInfo: data.stateInfo ?? data.location ?? "",
          country: data.country ?? "India",
        });
      } catch (err) {
        toast.error(err.message || "Could not load profile");
      } finally {
        setProfileLoading(false);
      }
    };

    if (isAuthenticated) loadProfile();
    else setProfileLoading(false);
  }, [isAuthenticated]);

  const displayName = useMemo(
    () => user.name?.trim() || authUser?.name || "Your account",
    [user.name, authUser?.name],
  );

  const startEdit = () => {
    setEditSnapshot({ ...user });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (editSnapshot) setUser(editSnapshot);
    setEditSnapshot(null);
    setIsEditing(false);
  };

  const handleChange = (e) => {
    setUser((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = String(user.name || "").trim();
    const email = String(user.email || "").trim();
    const phone = String(user.phone || "").trim();
    const addressLine = String(user.addressLine || "").trim();
    const city = String(user.city || "").trim();
    const stateInfo = String(user.stateInfo || "").trim();
    const country = String(user.country || "").trim() || "India";

    if (!name) return toast.error("Please enter your full name.");
    if (!isValidEmail(email)) return toast.error("Please enter a valid email.");
    if (!isValidPhoneDigits(phone)) return toast.error("Enter a valid phone (8–15 digits).");
    if (addressLine.length < 5) return toast.error("Street address is too short.");
    if (!city) return toast.error("Please enter your city.");
    if (!stateInfo) return toast.error("Please enter your state or region.");

    setLoading(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("addressLine", addressLine);
    formData.append("city", city);
    formData.append("location", stateInfo);
    formData.append("country", country);
    formData.append("bio", "");
    formData.append("interests", "[]");

    try {
      const res = await updateUserProfileService(formData);
      toast.success("Profile updated");
      if (res?.user) {
        setAuthUser((prev) => {
          const base = prev || {};
          const next = {
            ...base,
            id: res.user.id ?? base.id,
            name: res.user.name,
            email: res.user.email,
            phone: res.user.phone,
          };
          try {
            localStorage.setItem("user", JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
        setUser((prev) => ({
          ...prev,
          name: res.user.name ?? prev.name,
          email: res.user.email ?? prev.email,
          phone: res.user.phone ?? prev.phone,
          addressLine: res.user.addressLine ?? prev.addressLine,
          city: res.user.city ?? prev.city,
          stateInfo: res.user.location ?? prev.stateInfo,
          country: res.user.country ?? prev.country,
        }));
      }
      setEditSnapshot(null);
      setIsEditing(false);
    } catch (err) {
      toast.error(err.message || "Profile update failed");
    } finally {
      setLoading(false);
    }
  };

  const regionValue = user.stateInfo ?? "";

  if (profileLoading) {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 text-stone-500">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" aria-hidden />
        <p className="text-sm font-medium">Loading your profile…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-stone-200/90 bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 p-6 text-white shadow-lg shadow-orange-300/25 sm:p-8">
        <div
          className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border-2 border-white/30 bg-white/15 text-2xl font-bold shadow-lg backdrop-blur-sm sm:h-24 sm:w-24">
              {initials(user.name, user.email)}
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-widest text-orange-100">
                Account
              </p>
              <h2 className="mt-0.5 truncate text-xl font-bold tracking-tight sm:text-2xl">
                {displayName}
              </h2>
              {user.email ? (
                <p className="mt-1 truncate text-sm text-orange-50/90">{user.email}</p>
              ) : null}
            </div>
          </div>
          {!isEditing ? (
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-orange-700 shadow-md transition hover:bg-orange-50 active:scale-[0.98]"
            >
              <Pencil className="h-4 w-4" aria-hidden />
              Edit profile
            </button>
          ) : (
            <span className="inline-flex items-center rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
              Editing
            </span>
          )}
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="rounded-2xl border border-stone-200/90 bg-white p-5 shadow-sm sm:p-6">
            <h3 className="mb-1 text-base font-bold text-stone-900">Contact & delivery</h3>
            <p className="mb-5 text-xs text-stone-500">
              Used for orders and notifications. Fields marked * are required.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="profile-name" className={labelClass}>
                  Full name *
                </label>
                <input
                  id="profile-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  className={inputClass}
                  value={user.name}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="profile-email" className={labelClass}>
                    Email *
                  </label>
                  <input
                    id="profile-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className={inputClass}
                    value={user.email}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="profile-phone" className={labelClass}>
                    Phone *
                  </label>
                  <input
                    id="profile-phone"
                    name="phone"
                    type="tel"
                    required
                    className={inputClass}
                    value={user.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="profile-address" className={labelClass}>
                  Street address *
                </label>
                <input
                  id="profile-address"
                  name="addressLine"
                  type="text"
                  required
                  className={inputClass}
                  value={user.addressLine}
                  onChange={handleChange}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="profile-city" className={labelClass}>
                    City *
                  </label>
                  <input
                    id="profile-city"
                    name="city"
                    type="text"
                    required
                    className={inputClass}
                    value={user.city}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="profile-region" className={labelClass}>
                    State / region *
                  </label>
                  <input
                    id="profile-region"
                    name="stateInfo"
                    type="text"
                    required
                    className={inputClass}
                    value={regionValue}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="profile-country" className={labelClass}>
                  Country *
                </label>
                <input
                  id="profile-country"
                  name="country"
                  type="text"
                  required
                  className={inputClass}
                  value={user.country}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          <div className="sticky bottom-0 z-10 -mx-1 flex flex-col-reverse gap-2 rounded-2xl border border-stone-200/90 bg-white/95 p-4 shadow-lg backdrop-blur-sm sm:static sm:flex-row sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center rounded-xl border border-stone-200 px-6 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 text-sm font-bold text-white shadow-md shadow-orange-300/30 hover:from-orange-600 hover:to-amber-600 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <section className="rounded-2xl border border-stone-200/90 bg-white p-5 shadow-sm sm:p-6">
            <h3 className="mb-4 text-base font-bold text-stone-900">Contact & delivery</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoRow icon={User} label="Full name" value={user.name} required />
              <InfoRow icon={Mail} label="Email" value={user.email} required />
              <InfoRow icon={Phone} label="Phone" value={user.phone} required />
              <InfoRow icon={MapPin} label="Street" value={user.addressLine} required />
              <InfoRow icon={MapPin} label="City" value={user.city} required />
              <InfoRow icon={MapPin} label="State / region" value={regionValue} required />
              <InfoRow icon={Globe} label="Country" value={user.country} required />
            </div>
          </section>

          <p className="rounded-xl border border-dashed border-stone-200 bg-stone-50/80 px-4 py-3 text-xs leading-relaxed text-stone-600">
            Manage saved delivery addresses in the{" "}
            <span className="font-semibold text-stone-800">Addresses</span> tab. This profile is
            your default contact for new orders.
          </p>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
